import { contentStateFromString, contentStateToString } from "@utils/richText/converters";
import { MediaDto, mediaFromDto, mediaToDto } from "@domain/media/dtos";
import { OrganizationDto, organizationFromDto, organizationToDto } from "@domain/organization/dtos";
import { PersonDto, personFromDto, personToDto, ProfileDto, profileFromDto } from "@domain/user/dtos";
import { Group } from "./group";
import { GroupDraft } from "./groupDraft";
import { GroupMember } from "./groupMember";
import {
	ShareroomPresentationDto,
	shareroomPresentationFromDto,
	shareroomPresentationToDto,
} from "@domain/group/shareroom/dto";

export interface GroupDraftDto extends Omit<GroupDraft, "banner" | "organization" | "rules"> {
	bannerId?: string;
	organizationId?: string;
	rules?: string;
}

export interface GroupDto extends Omit<
	Group,
	| "banner"
	| "administrators"
	| "organization"
	| "rules"
	| "presentationAsset"
	| "presentation"
	| "targetAudience"
	| "objective"
	| "schedule"
	| "formatCourse"
> {
	banner?: MediaDto;
	administrators: PersonDto[];
	organization?: OrganizationDto;
	rules?: string;
	presentationAsset?: ShareroomPresentationDto;
	presentation?: string;
	targetAudience?: string;
	objective?: string;
	schedule?: string;
	formatCourse?: string;
}

export function groupDraftToDto(draft: GroupDraft): GroupDraftDto {
	return {
		...draft,
		bannerId: draft.banner?.id,
		organizationId: draft.organization?.id,
		rules: draft.rules.getPlainText().trim() ? contentStateToString(draft.rules) : undefined,
	};
}

export function groupFromDto(dto: GroupDto): Group {
	return {
		...dto,
		banner: dto.banner ? mediaFromDto(dto.banner) : undefined,
		administrators: dto.administrators.map((admin) => personFromDto(admin)),
		organization: dto.organization ? organizationFromDto(dto.organization) : undefined,
		rules: contentStateFromString(dto.rules ?? ""),
		presentationAsset: dto.presentationAsset ? shareroomPresentationFromDto(dto.presentationAsset) : undefined,
		presentation: contentStateFromString(dto.presentation ?? ""),
		targetAudience: contentStateFromString(dto.targetAudience ?? ""),
		objective: contentStateFromString(dto.objective ?? ""),
		schedule: contentStateFromString(dto.schedule ?? ""),
		formatCourse: contentStateFromString(dto.formatCourse ?? ""),
	};
}

export function groupToDto(group: Group): GroupDto {
	const { presentation, targetAudience, objective, schedule, formatCourse } = group;
	return {
		...group,
		administrators: group.administrators.map((admin) => personToDto(admin)),
		organization: group.organization ? organizationToDto(group.organization) : undefined,
		banner: group.banner && mediaToDto(group.banner),
		presentationAsset: group.presentationAsset && shareroomPresentationToDto(group.presentationAsset),
		rules: group.rules.getPlainText().trim() ? contentStateToString(group.rules) : undefined,
		presentation: presentation && presentation.getPlainText().trim() ? contentStateToString(presentation) : undefined,
		targetAudience:
			targetAudience && targetAudience.getPlainText().trim() ? contentStateToString(targetAudience) : undefined,
		objective: objective && objective.getPlainText().trim() ? contentStateToString(objective) : undefined,
		schedule: schedule && schedule.getPlainText().trim() ? contentStateToString(schedule) : undefined,
		formatCourse: formatCourse && formatCourse.getPlainText().trim() ? contentStateToString(formatCourse) : undefined,
	};
}

export type GroupMemberDto = ProfileDto & Pick<GroupMember, "status" | "role">;

export function groupMemberFromDto({ status, role, ...dto }: GroupMemberDto): GroupMember {
	return { ...profileFromDto(dto), status, role };
}
